import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../hooks/useAppConfig';
import { useLanguages } from '../hooks/useLanguages';
import appLogo from '../assets/branding/logo.png';
import LanguageFlag from './LanguageFlag';

function getInitials(user) {
  const label = (user?.name || user?.email || '?').trim();
  const words = label.split(/\s+/);
  return (words.length >= 2 ? words[0][0] + words[1][0] : label.slice(0, 2)).toUpperCase();
}

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, languagePrefs, openLanguagePreferences } = useAuth();
  const { languages } = useLanguages();
  const { t } = useTranslation();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const writingStep = config.learningSteps.find((step) => step.code === 'writing');

  const TOOL_LINKS = [
    { to: '/learn/conversations',      label: t('tools.conversations'),      icon: '🗣️', desc: t('tools.conversationsDesc') },
    { to: '/learn/vocabulary',         label: t('tools.vocabulary'),         icon: '📚', desc: t('tools.vocabularyDesc') },
    { to: '/learn/grammar',            label: t('tools.grammar'),            icon: '📖', desc: t('tools.grammarDesc') },
    { to: '/learn/writing',            label: writingStep?.label ?? 'Writing', icon: writingStep?.icon ?? '✍️', desc: writingStep?.desc ?? 'Practice useful real-world writing.' },
    { to: '/learn/common-foundations', label: t('tools.commonFoundations'),  icon: '📘', desc: t('tools.commonFoundationsDesc') },
    { to: languagePrefs?.targetLanguage ? `/listen/${languagePrefs.targetLanguage}` : '/', label: t('tools.listenRead'), icon: '🎧', desc: t('tools.listenReadDesc') },
  ];

  const langMap = useMemo(() => Object.fromEntries(languages.map((l) => [l.code, l])), [languages]);
  const nativeLang = languagePrefs ? langMap[languagePrefs.nativeLanguage] : null;
  const targetLang = languagePrefs ? langMap[languagePrefs.targetLanguage] : null;

  useEffect(() => setIsMenuOpen(false), [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const inLearn = location.pathname.startsWith('/learn');
  const inStaticPages = location.pathname === '/quick-sheet';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
    <div className="w-full bg-amber-50 border-b border-amber-200 px-4 py-1.5 text-center text-xs text-amber-800">
      {t('disclaimer')}
    </div>
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/92 backdrop-blur-lg">
      <nav className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">

        {/* ── Brand + primary links ── */}
        <div className="flex items-center gap-4">
          <Link to="/" className="group flex min-w-0 items-center gap-2.5">
            <img
              src={appLogo}
              alt="SublingoX"
              className="h-9 w-9 rounded-xl object-cover ring-2 ring-indigo-100 transition group-hover:ring-indigo-300"
            />
            <span className="truncate text-base font-black text-slate-900 sm:text-lg">
              <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
                SublingoX
              </span>
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden items-center gap-1 md:flex">
            <Link
              to="/"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/') ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.home')}
              {isActive('/') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            {/* Learn dropdown */}
            <div className="group relative">
              <button
                type="button"
                aria-haspopup="true"
                className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  inLearn ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                {t('nav.learn')}
                <svg className="ml-1 inline-block h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                {inLearn && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
              </button>

              <div className="invisible absolute left-0 top-full mt-2.5 w-72 rounded-2xl border border-slate-200 bg-white p-2 opacity-0 shadow-2xl shadow-slate-900/10 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                <div className="mb-1 px-3 pt-1 pb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  {t('nav.chooseTrack')}
                </div>
                {TOOL_LINKS.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-slate-50 ${isActive(item.to) ? 'bg-indigo-50' : ''}`}
                  >
                    <span className="mt-0.5 text-lg leading-none">{item.icon}</span>
                    <span>
                      <p className={`text-sm font-bold leading-tight ${isActive(item.to) ? 'text-indigo-700' : 'text-slate-800'}`}>
                        {item.label}
                      </p>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {isAuthenticated && (
              <Link
                to="/exercises"
                className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  isActive('/exercises') ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                {t('exercise.dailyTests')}
                {isActive('/exercises') && (
                  <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />
                )}
              </Link>
            )}

            <Link
              to="/download"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/download') ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.downloadApp')}
              {isActive('/download') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            <Link
              to="/quick-sheet"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                inStaticPages ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.quickSheet')}
              {inStaticPages && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>
          </div>
        </div>

        {/* ── Auth controls ── */}
        <div className="hidden items-center gap-2 md:flex">
          {/* Language pair pill */}
          {nativeLang && targetLang ? (
            <button
              onClick={openLanguagePreferences}
              title={t('nav.setLanguages')}
              className="flex items-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100"
            >
              <span className="flex items-center gap-1.5"><LanguageFlag flag={nativeLang.flag} label={nativeLang.label} />{nativeLang.label}</span>
              <svg className="h-3 w-3 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              <span className="flex items-center gap-1.5"><LanguageFlag flag={targetLang.flag} label={targetLang.label} />{targetLang.label}</span>
            </button>
          ) : (
            <button
              onClick={openLanguagePreferences}
              title={t('nav.setLanguages')}
              className="flex items-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100"
            >
              <span>🌍</span>
              <span>{t('nav.setLanguages')}</span>
            </button>
          )}

          {isAuthenticated ? (
            <>
              <button
                onClick={() => navigate('/profile')}
                title={user?.name || user?.email}
                aria-label={t('profile.title')}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-black text-white shadow-md transition hover:shadow-lg hover:scale-105 select-none"
              >
                {getInitials(user)}
              </button>
              <button
                onClick={handleLogout}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="rounded-xl px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-bold text-white shadow-md shadow-indigo-600/20 transition hover:-translate-y-px hover:shadow-indigo-600/40"
              >
                {t('nav.getStarted')}
              </Link>
            </>
          )}
        </div>

        {/* ── Mobile menu toggle ── */}
        <button
          type="button"
          onClick={() => setIsMenuOpen((v) => !v)}
          className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 md:hidden"
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation-menu"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {isMenuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            }
          </svg>
        </button>
      </nav>

      {/* ── Mobile drawer ── */}
      {isMenuOpen && (
        <div
          id="mobile-navigation-menu"
          className="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t border-slate-100 bg-white/98 backdrop-blur-lg md:hidden"
        >
          <div
            className="space-y-1 px-4 pt-4"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          >
            {/* Mobile language pair pill */}
            {nativeLang && targetLang ? (
              <button
                type="button"
                onClick={openLanguagePreferences}
                className="mb-2 flex w-full min-w-0 items-center justify-between gap-2 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="flex min-w-0 items-center gap-1.5"><LanguageFlag flag={nativeLang.flag} label={nativeLang.label} /><span className="truncate">{nativeLang.label}</span></span>
                  <svg className="h-3 w-3 shrink-0 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <span className="flex min-w-0 items-center gap-1.5"><LanguageFlag flag={targetLang.flag} label={targetLang.label} /><span className="truncate">{targetLang.label}</span></span>
                </span>
                <span className="shrink-0 text-[10px] font-semibold text-indigo-400">{t('nav.tapToChange')}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={openLanguagePreferences}
                className="mb-2 flex w-full min-w-0 items-center justify-between gap-2 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="shrink-0">🌍</span>
                  <span className="truncate">{t('nav.setLanguages')}</span>
                </span>
                <span className="shrink-0 text-[10px] font-semibold text-indigo-400">{t('nav.tapToChoose')}</span>
              </button>
            )}

            {/* Mobile brand row */}
            <div className="mb-3 flex items-center gap-2.5 rounded-xl bg-gradient-to-r from-indigo-50 to-violet-50 p-2.5">
              <img src={appLogo} alt="SublingoX" className="h-9 w-9 rounded-lg object-cover ring-1 ring-indigo-200" />
              <div>
                <p className="text-sm font-black text-slate-900">SublingoX</p>
                <p className="text-xs text-slate-500">{t('nav.languagePlatform')}</p>
              </div>
            </div>

            <Link
              to="/"
              className={`block rounded-xl px-3 py-2.5 text-sm font-bold transition ${isActive('/') ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              {t('nav.home')}
            </Link>

            {isAuthenticated && (
              <Link
                to="/exercises"
                className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                  isActive('/exercises') ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span>✅</span>
                <span>{t('exercise.dailyTests')}</span>
              </Link>
            )}

            <Link
              to="/download"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                isActive('/download') ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>📱</span>
              <span>{t('nav.downloadApp')}</span>
            </Link>

            <div className="px-3 pt-3 pb-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
              {t('nav.learn')}
            </div>
            {TOOL_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${isActive(item.to) ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'}`}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            ))}

            <Link
              to="/quick-sheet"
              className={`mt-2 flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${isActive('/quick-sheet') ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              <span aria-hidden="true">▤</span>
              <span>{t('nav.quickSheet')}</span>
            </Link>

            <div className="mt-3 border-t border-slate-100 pt-3">
              {isAuthenticated ? (
                <>
                  <div className="mb-2 flex items-center gap-2.5 px-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-[10px] font-black text-white select-none">
                      {getInitials(user)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-800">{user?.name || t('profile.learner')}</p>
                      <p className="truncate text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/profile')}
                    className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    {t('nav.viewProfile')}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-700 hover:bg-slate-50"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/login" className="rounded-xl border border-slate-200 py-2.5 text-center text-sm font-bold text-slate-700 hover:bg-slate-50">
                    {t('nav.login')}
                  </Link>
                  <Link to="/register" className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 py-2.5 text-center text-sm font-bold text-white">
                    {t('nav.getStarted')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
    </>
  );
}
